/**
 * NINETEENSTARZ product card interaction layer.
 *
 * Two purely-decorative desktop behaviours layered on top of the native
 * product-card component:
 *
 * 1. A small "View" label that follows the pointer while it's over a product
 *    card, replacing the system cursor there.
 * 2. A subtle mouse-position image tilt/parallax on any element marked
 *    `data-ns-tilt` (used by the NS Tees / Long Sleeve editorial showcase
 *    sections), driven by `--ns-tilt-x` / `--ns-tilt-y` custom properties that
 *    the section's own CSS reads to translate the image.
 *
 * Neither behaviour ever initializes on touch/coarse pointers or when the
 * visitor prefers reduced motion, and both degrade to nothing (default
 * cursor, static images) if either media query isn't met. See
 * snippets/product-card-styles.liquid for the matching `.ns-cursor` /
 * `html.ns-cursor-active` styles.
 */
const supportsFinePointer = window.matchMedia('(any-pointer: fine)').matches;
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (supportsFinePointer && !prefersReducedMotion) {
  /** @type {HTMLElement | null} */
  let cursorEl = null;
  /** @type {Element | null} */
  let activeCard = null;
  /** @type {HTMLElement | null} */
  let tiltEl = null;
  /** @type {number | null} */
  let frame = null;
  let pointerX = 0;
  let pointerY = 0;

  function getCursorEl() {
    if (cursorEl) return cursorEl;

    cursorEl = document.createElement('div');
    cursorEl.className = 'ns-cursor';
    cursorEl.setAttribute('aria-hidden', 'true');
    cursorEl.textContent = window.Theme?.translations?.view_product ?? 'View';
    document.body.appendChild(cursorEl);

    return cursorEl;
  }

  function renderPosition() {
    frame = null;

    if (cursorEl) {
      cursorEl.style.left = `${pointerX}px`;
      cursorEl.style.top = `${pointerY}px`;
    }

    if (tiltEl) {
      const rect = tiltEl.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const relativeX = (pointerX - rect.left) / rect.width - 0.5;
        const relativeY = (pointerY - rect.top) / rect.height - 0.5;
        tiltEl.style.setProperty('--ns-tilt-x', relativeX.toFixed(3));
        tiltEl.style.setProperty('--ns-tilt-y', relativeY.toFixed(3));
      }
    }
  }

  function schedulePositionUpdate() {
    if (frame == null) frame = requestAnimationFrame(renderPosition);
  }

  /** @param {PointerEvent} event */
  function handlePointerOver(event) {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const card = event.target.closest('.product-card .card-gallery');
    if (card && card !== activeCard) {
      activeCard = card;
      document.documentElement.classList.add('ns-cursor-active');
      getCursorEl().classList.add('ns-cursor--visible');
    }

    const tilt = event.target.closest('[data-ns-tilt]');
    if (tilt instanceof HTMLElement && tilt !== tiltEl) {
      tiltEl = tilt;
    }
  }

  /** @param {PointerEvent} event */
  function handlePointerOut(event) {
    if (!(event.target instanceof Element)) return;
    const related = event.relatedTarget;

    if (activeCard) {
      const card = event.target.closest('.product-card .card-gallery');
      if (card === activeCard && !(related instanceof Node && activeCard.contains(related))) {
        activeCard = null;
        document.documentElement.classList.remove('ns-cursor-active');
        cursorEl?.classList.remove('ns-cursor--visible');
      }
    }

    if (tiltEl) {
      const tilt = event.target.closest('[data-ns-tilt]');
      if (tilt === tiltEl && !(related instanceof Node && tiltEl.contains(related))) {
        tiltEl.style.setProperty('--ns-tilt-x', '0');
        tiltEl.style.setProperty('--ns-tilt-y', '0');
        tiltEl = null;
      }
    }
  }

  /** @param {PointerEvent} event */
  function handlePointerMove(event) {
    if ((!activeCard && !tiltEl) || event.pointerType !== 'mouse') return;
    pointerX = event.clientX;
    pointerY = event.clientY;
    schedulePositionUpdate();
  }

  document.addEventListener('pointerover', handlePointerOver);
  document.addEventListener('pointerout', handlePointerOut);
  document.addEventListener('pointermove', handlePointerMove);
}
